/**
 * Check 10 — `image_subject`. Is the picture about the thing the caption is about?
 *
 * THE DEFECT THIS EXISTS FOR, measured on real artifacts (2026-07-31/08-01):
 *   caption "Stop using beans roasted yesterday … the science of degassing"
 *     → image: a woman's hands holding a latte
 *   caption "Washed vs Natural processing … espresso extraction"
 *     → image: a French press
 *   caption "The exact parameters to dial in … 18g dose"
 *     → image: a woman in a blazer at a sidewalk café
 * Four of four mismatched in one run, three of four off-category in the next.
 * It survived two full evaluation runs UNMEASURED because nothing compared the
 * two texts — the caption and the image brief were built in parallel from the
 * same upstream blob and never reconciled.
 *
 * WHY LEXICAL, AND WHAT THAT HONESTLY BUYS. Both sides are text and both are in
 * hand at write time, so this needs no model call, no network and no image
 * bytes. It measures ONE thing: whether the subject the image was commissioned
 * to show shares any concrete noun with the caption it will be published under.
 * That is a proxy — a caption and a brief can share words and still disagree —
 * so a `pass` here NAMES the shared terms rather than asserting coherence, and
 * a human can overrule it in one glance. What it cannot do is miss the observed
 * failure: "degassing / CO2 / valve" against "woman, latte, café table" shares
 * nothing, and zero shared terms is not a judgement call.
 *
 * IT MUST NEVER FABRICATE A PASS. `imageSubject` absent ⇒ `skipped`, never
 * `pass` — every post generated before this shipped has no record of what its
 * image was asked to show, and reporting "coherent" for a comparison that never
 * ran is the exact bug the grader exists to end. Same discipline as
 * `sourceRatio` in checks/aspect.ts.
 *
 * Same hard constraint as every other module here: React-free, DOM-free,
 * Node-free, `@/`-alias-free, relative imports with explicit `.ts`, so Deno can
 * reach it (the generator imports the term functions directly to verify its own
 * derivation at write time).
 */
import { CHECK_LABEL, type QualityCheck } from '../types.ts';

/**
 * Words that carry no subject. Ordinary function words plus the copywriting
 * connective tissue every caption has.
 */
const STOPWORDS: ReadonlySet<string> = new Set([
  'the',
  'and',
  'for',
  'you',
  'your',
  'our',
  'with',
  'that',
  'this',
  'these',
  'those',
  'from',
  'into',
  'onto',
  'are',
  'was',
  'were',
  'been',
  'being',
  'has',
  'have',
  'had',
  'not',
  'but',
  'all',
  'any',
  'can',
  'will',
  'just',
  'more',
  'most',
  'some',
  'than',
  'then',
  'them',
  'they',
  'their',
  'there',
  'here',
  'what',
  'when',
  'where',
  'which',
  'who',
  'why',
  'how',
  'get',
  'got',
  'out',
  'off',
  'over',
  'under',
  'about',
  'after',
  'before',
  'again',
  'every',
  'each',
  'own',
  'too',
  'very',
  'one',
  'two',
  'now',
  'new',
  'make',
  'made',
  'making',
  'take',
  'taken',
  'know',
  'like',
  'want',
  'need',
  'love',
  'best',
  'better',
  'good',
  'great',
  'really',
  'still',
  'because',
  'while',
  'through',
  'between',
  'both',
  'other',
  'another',
  'same',
  'such',
  'only',
  'also',
  'its',
  'his',
  'her',
  'hers',
  'him',
  'she',
  'his',
  'ours',
  'yours',
  'mine',
  'weve',
  'youre',
  'dont',
  'doesnt',
  'isnt',
  'wasnt',
  'cant',
  'wont',
  'thats',
  'heres',
  'theres',
  'lets',
  'link',
  'bio',
  'comment',
  'below',
  'share',
  'save',
  'shop',
  'tap',
  'click',
  'follow',
  'dm',
  'post',
  'caption',
  'social',
  'media',
  'brand',
  'story',
  'today',
  'day',
  'days',
  'week',
  'thing',
  'things',
  'way',
  'ways',
  'time',
  'times',
  'people',
  'someone',
  'anyone',
  'everyone',
]);

/**
 * Vocabulary that belongs to the CAMERA, not to the subject. An overlap that
 * exists only because both texts said "warm light" is a fake overlap — this is
 * the term set our own image-prompt scaffolding injects, so leaving it in would
 * let the check pass itself.
 */
const PHOTOGRAPHIC_TERMS: ReadonlySet<string> = new Set([
  'photo',
  'photograph',
  'photography',
  'photorealistic',
  'image',
  'picture',
  'shot',
  'shoot',
  'frame',
  'framing',
  'framed',
  'compose',
  'composition',
  'crop',
  'angle',
  'lens',
  'aperture',
  'macro',
  'closeup',
  'close',
  'wide',
  'depth',
  'field',
  'bokeh',
  'focus',
  'focal',
  'exposure',
  'light',
  'lighting',
  'lit',
  'backlight',
  'backlit',
  'rim',
  'diffuse',
  'diffused',
  'ambient',
  'natural',
  'soft',
  'warm',
  'cool',
  'moody',
  'dramatic',
  'cinematic',
  'editorial',
  'minimal',
  'clean',
  'background',
  'backdrop',
  'foreground',
  'surface',
  'scene',
  'setting',
  'shallow',
  'golden',
  'hour',
  'daylight',
  'sunlight',
  'shadow',
  'shadows',
  'highlight',
  'tone',
  'tones',
  'color',
  'colour',
  'colors',
  'colours',
  'palette',
  'texture',
  'textures',
  'detail',
  'details',
  'flatlay',
  'overhead',
  'top',
  'down',
  'side',
  'studio',
  'seamless',
  'style',
  'styled',
  'aesthetic',
  'mood',
  'vibe',
  'render',
  'rendered',
  'visual',
  'visuals',
  'subject',
  'shows',
  'showing',
  'featuring',
  'centered',
  'centred',
  'placed',
  'resting',
  'sitting',
  'holding',
]);

/**
 * Crude, deliberate, and documented: lowercase, strip a possessive, collapse a
 * regular plural, collapse -ing / -ed, then undo a doubled final consonant so
 * `degassing → degas` and `roasted / roasting → roast` land on one another. No
 * dictionary, no dependency, no runtime cost. It over-merges occasionally
 * (`beans` and `bean`, intended; `pressing` and `press`, intended) and that is
 * the right direction of error for a check whose FAIL case is "zero terms in
 * common" — over-merging makes a false FAIL less likely, never more.
 */
export function stemTerm(raw: string): string {
  let w = raw.toLowerCase().replace(/['’]s$/, '');
  if (w.length > 5 && w.endsWith('ies')) w = `${w.slice(0, -3)}y`;
  // `-es` is only a plural marker after a sibilant (boxes → box, brushes →
  // brush). Applied blindly it turns `valves` into `valv`, which then fails to
  // match `valve` — a missed overlap, i.e. a false FAIL.
  else if (w.length > 4 && /(?:s|x|z|ch|sh)es$/.test(w)) w = w.slice(0, -2);
  else if (w.length > 3 && w.endsWith('s') && !w.endsWith('ss')) w = w.slice(0, -1);
  // The de-doubling only follows an -ing/-ed strip (running → runn → run,
  // degassing → degass → degas). Unconditionally it would eat the real final
  // letter of `valve` and `glass`, which is a false merge in the direction that
  // makes a false PASS more likely — the one error this check must not make.
  let stripped = false;
  if (w.length > 5 && w.endsWith('ing')) {
    w = w.slice(0, -3);
    stripped = true;
  } else if (w.length > 4 && w.endsWith('ed')) {
    w = w.slice(0, -2);
    stripped = true;
  }
  if (stripped && w.length > 3 && /(.)\1$/.test(w)) w = w.slice(0, -1);
  return w;
}

/**
 * The concrete terms a text commits to. Hashtags count (`#degassing` is the
 * subject as much as the prose is); URLs, mentions, emoji and digits do not.
 */
export function contentTerms(text: string): string[] {
  if (!text) return [];
  const cleaned = text
    .replace(/https?:\/\/\S+/g, ' ')
    .replace(/@[\w.]+/g, ' ')
    .replace(/#/g, ' ')
    .replace(/[^\p{L}\p{N}'’]+/gu, ' ');

  const seen = new Set<string>();
  const out: string[] = [];
  for (const token of cleaned.split(/\s+/)) {
    if (token.length < 3) continue;
    const lower = token.toLowerCase();
    if (STOPWORDS.has(lower) || PHOTOGRAPHIC_TERMS.has(lower)) continue;
    const stem = stemTerm(token);
    if (stem.length < 3) continue;
    if (STOPWORDS.has(stem) || PHOTOGRAPHIC_TERMS.has(stem)) continue;
    if (seen.has(stem)) continue;
    seen.add(stem);
    out.push(stem);
  }
  return out;
}

/** Stems present in BOTH texts, in caption order. */
export function sharedSubjectTerms(caption: string, imageSubject: string): string[] {
  const subject = new Set(contentTerms(imageSubject));
  if (subject.size === 0) return [];
  return contentTerms(caption).filter((term) => subject.has(term));
}

export interface ImageSubjectCheckInput {
  caption: string;
  mediaUrls: string[];
  /**
   * The SUBJECT the image was commissioned to show — the scene text actually
   * sent to the image model, not the topic it was supposed to come from.
   * Undefined for every post generated before this shipped, and for every
   * uploaded image: those produce `skipped`.
   */
  imageSubject?: string | null;
  /**
   * Where the brief came from. `caption` means it was read out of the finished
   * caption with its quotes verified; `topic` means the derivation was
   * unavailable and the slot topic was used instead.
   *
   * THIS EXISTS TO STOP AN OVERCLAIM. The caption is itself written from the
   * topic, so a topic-derived brief shares words with the caption almost by
   * construction — the overlap is close to guaranteed and therefore proves much
   * less. Verified live on 2026-08-01: 12 of 12 generations fell back to the
   * topic and every one of them scored `pass`. The status stays (the brief is
   * real and the comparison did run), but the sentence must not imply the
   * picture was drawn from the caption when it was not.
   */
  imageSubjectSource?: 'caption' | 'topic' | null;
}

export function checkImageSubject(input: ImageSubjectCheckInput): QualityCheck {
  const base = { id: 'image_subject' as const, label: CHECK_LABEL.image_subject };
  const caption = (input.caption ?? '').trim();
  const subject = (input.imageSubject ?? '').trim();
  const hasMedia = Array.isArray(input.mediaUrls) && input.mediaUrls.length > 0;

  if (!hasMedia) {
    return {
      ...base,
      status: 'skipped',
      detail: 'This post has no image to compare the caption to.',
    };
  }
  if (!caption) {
    return { ...base, status: 'skipped', detail: 'No caption to check yet.' };
  }
  if (!subject) {
    return {
      ...base,
      status: 'skipped',
      detail:
        'Mora has no record of what this image was asked to show, so it cannot say whether the picture matches the caption. Regenerating the image records it.',
    };
  }

  const shared = sharedSubjectTerms(caption, subject);
  const captionTerms = contentTerms(caption);
  const subjectTerms = contentTerms(subject);
  const preview = subject.length > 120 ? `${subject.slice(0, 117)}…` : subject;

  if (shared.length >= 2) {
    const terms = shared.slice(0, 4).join(', ');
    return {
      ...base,
      status: 'pass',
      detail:
        input.imageSubjectSource === 'topic'
          ? `The image was commissioned to show ${terms}, which the caption is also about. Both came from the post's topic rather than the image being drawn from the finished caption, so this is a weaker match than it looks.`
          : `The image was commissioned to show ${terms} — read straight out of this caption, so the picture and the words are about the same thing.`,
    };
  }

  if (shared.length === 1) {
    return {
      ...base,
      status: 'warn',
      detail: `The caption and the image share only one word — "${shared[0]}". The image brief was "${preview}". Worth checking the picture is really about what the caption promises.`,
    };
  }

  // Zero overlap. Only call it broken when there was genuinely enough material
  // on both sides for an overlap to have happened; a five-word caption or a
  // two-word brief is a gap in the data, not a proven mismatch.
  if (captionTerms.length >= 8 && subjectTerms.length >= 3) {
    return {
      ...base,
      status: 'fail',
      detail: `The image is not about the caption. The caption is about ${captionTerms.slice(0, 4).join(', ')}; the image was commissioned as "${preview}" — they have no subject in common.`,
    };
  }

  return {
    ...base,
    status: 'warn',
    detail: `The caption and the image brief ("${preview}") have no words in common, but there is too little text on one side to be sure they disagree.`,
  };
}
